import { createRef, useCallback, useEffect, useRef, useState } from "react";
import { updateMetadata, uploadFile } from "../../../../utils/storage";
import Button from "../../../../components/Button/Button";
import SimpleModal from "../../../../components/Modal/SimpleModal/SimpleModal";
import { CheckMark, CloseIcon, PauseIcon, PlayButton } from "../../../../components/Svgs";
import classes from "./AudioTracksPopup.module.css";
import { getFileUrl } from "../../../../utils/api";
import slugify from "slugify";
import { useSelector } from "react-redux";

const AudioTracksPopup = ({
  musicPopup,
  setMusicPopup,
  availableAudioTracks,
  availableAudioTracksHandler,
  selectedAudioTrack,
  setSelectedAudioTrack,
  audioTrackLoaderHandler,
}) => {
  const { orgData } = useSelector((state) => state.orgData);
  const [popupSelectedAudioTrack, setPopupSelectedAudioTrack] = useState();
  const [playingAudioTrack, setPlayingAudioTrack] = useState();
  const audioTracksRefs = useRef(availableAudioTracks?.map(() => createRef()));
  const [audioProgressBars, setAudioProgressBars] = useState(["0", "0", "0"]);
  const orgId = orgData.org.orgId;
  const activityId = orgData.activities.activityId;

  const uploadTrackHandler = async (e) => {
    const originalFile = e.target.files[0];

    // Validate file type
    const isMp3 = /\.(mp3)$/i.test(originalFile.name);
    if (!isMp3) {
      alert("Not a valid audio file");
      return;
    }

    audioTrackLoaderHandler(true);

    // Sanitize and rename file
    const sanitizedFileName = `${Date.now()}_${slugify(originalFile.name, { lower: true })}`;
    const file = new File([originalFile], sanitizedFileName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    });

    // Get track duration
    let trackDuration;
    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.src = URL.createObjectURL(originalFile);
    audio.onloadedmetadata = () => {
      window.URL.revokeObjectURL(audio.src);
      trackDuration = audio.duration;

      setMusicPopup(false);

      // Upload flow starts after we get duration
      getFileUrl(orgId, activityId, file.name).then((data) => {
        uploadFile(file, data.url + data.sas_token).then(() => {
          updateMetadata(file, data.url + data.sas_token, "duration", Math.round(trackDuration)).then(() => {
            const newTrack = {
              name: originalFile.name, // Keep original name for UI
              url: data.url,
              duration: Math.round(trackDuration),
              id: availableAudioTracks.length + 1,
            };

            availableAudioTracksHandler([...availableAudioTracks, newTrack]);
            setSelectedAudioTrack(newTrack);
            audioTrackLoaderHandler(false);
          });
        });
      });
    };
  };

  useEffect(() => {
    setPopupSelectedAudioTrack(selectedAudioTrack);
  }, [selectedAudioTrack]);

  const playingAudioTrackHandler = useCallback(
    (id) => {
      /* pause the old one */
      audioTracksRefs?.current[playingAudioTrack]?.current?.pause();

      /* play the new one */
      audioTracksRefs?.current[id]?.current?.play();
      setPlayingAudioTrack(id);
    },
    [playingAudioTrack]
  );

  useEffect(() => {
    var intervalId;
    if (
      audioTracksRefs.current !== undefined &&
      !audioTracksRefs?.current[playingAudioTrack]?.current?.paused &&
      audioTracksRefs?.current[playingAudioTrack]?.current?.paused !== undefined
    ) {
      intervalId = setInterval(() => {
        setAudioProgressBars(
          audioTracksRefs.current.map((i) =>
            ((Number(i?.current?.currentTime.toFixed()) / Number(i?.current?.duration.toFixed())) * 100).toFixed(1)
          )
        );
      }, 800);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [playingAudioTrackHandler, audioTracksRefs, playingAudioTrack]);

  const closeMusicPopupHandler = () => {
    setMusicPopup(false);
    setPopupSelectedAudioTrack(selectedAudioTrack);
    playingAudioTrackHandler(null);
  };

  if (audioTracksRefs.current === undefined) {
    return;
  }

  return (
    <SimpleModal closeModalHandler={closeMusicPopupHandler} active={musicPopup}>
      <div className={classes.main}>
        <div className={classes.header}>
          <h2>SELECT AUDIO TRACK</h2>
          <CloseIcon onClick={closeMusicPopupHandler} />
        </div>
        {availableAudioTracks?.map((i, n) => (
          <div
            key={i.url}
            className={`${classes.track} ${i.url === popupSelectedAudioTrack?.url && classes["selected-track"]} ${
              playingAudioTrack === i.id && classes.playing
            } `}
          >
            <div>
              <div
                className={classes.img}
                onClick={() => playingAudioTrackHandler(playingAudioTrack === i.id ? null : i.id)}
              >
                {i.id === playingAudioTrack ? <PauseIcon /> : <PlayButton />}
              </div>
              <div>
                <h4>{i.name}</h4>
                <span>
                  {i.duration
                    ? new Date(Number(i.duration).toFixed() * 1000)?.toISOString()?.substring(14, 19)
                    : new Date(Number("00").toFixed() * 1000)?.toISOString()?.substring(14, 19)}
                </span>
              </div>
            </div>
            <div
              className={classes.check}
              onClick={() => setPopupSelectedAudioTrack({ name: i.name, duration: i.duration, url: i.url })}
            >
              <CheckMark />
            </div>
            <audio controls ref={audioTracksRefs?.current[n]} key={i.url}>
              <source src={i.url} type="audio/mp3" />
            </audio>
            <span
              className={classes["progress-bar"]}
              style={{
                width: `${audioProgressBars[i.id]}%`,
              }}
            ></span>
          </div>
        ))}
        <label htmlFor="new-track">Upload own track</label>
        <input type={"file"} onChange={uploadTrackHandler} hidden id="new-track" accept="audio/mp3" />
        <Button
          status={"active"}
          text="Select"
          onClick={() => {
            setSelectedAudioTrack(popupSelectedAudioTrack);
            setMusicPopup(false);
            playingAudioTrackHandler(null);
          }}
        />
      </div>
    </SimpleModal>
  );
};

export default AudioTracksPopup;
